import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

interface BrandLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  variant?: 'full' | 'icon-only';
}

const BrandLogo: React.FC<BrandLogoProps> = ({
  className = '',
  size = 'md',
  showText = true,
  variant = 'full'
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [brandConfig, setBrandConfig] = useState<{
    logo_url?: string;
    logo_dark_url?: string;
    primary_color?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const sizeClasses = {
    sm: 'h-6',
    md: 'h-8',
    lg: 'h-10',
    xl: 'h-12'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  useEffect(() => {
    const fetchBrandConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token && user?.org_id) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branding/logo`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const config = await response.json();
            setBrandConfig(config);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch brand config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrandConfig();
  }, [user?.org_id]);

  const getLogoUrl = () => {
    if (brandConfig) {
      if (theme === 'dark' && brandConfig.logo_dark_url) {
        return brandConfig.logo_dark_url.startsWith('http') 
          ? brandConfig.logo_dark_url 
          : `${process.env.REACT_APP_BACKEND_URL}${brandConfig.logo_dark_url}`;
      }
      if (brandConfig.logo_url) {
        return brandConfig.logo_url.startsWith('http')
          ? brandConfig.logo_url
          : `${process.env.REACT_APP_BACKEND_URL}${brandConfig.logo_url}`;
      }
    }
    
    // Fallback to default logos
    return theme === 'dark' 
      ? '/brand/agri-safe-logo-dark.png'
      : '/brand/agri-safe-logo.png';
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className={`bg-[var(--gray-200)] rounded animate-pulse ${sizeClasses[size]} w-8`} />
        {showText && variant === 'full' && (
          <div className={`bg-[var(--gray-200)] rounded animate-pulse h-4 w-20`} />
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-3 ${className}`} data-testid="brand-logo">
      <img
        src={getLogoUrl()}
        alt="Agri Safe"
        className={`${sizeClasses[size]} w-auto object-contain ${
          theme === 'dark' && !brandConfig?.logo_dark_url ? 'filter brightness-0 invert' : ''
        }`}
        onError={(e) => {
          // Fallback to default logo on error
          const target = e.target as HTMLImageElement;
          target.src = theme === 'dark' 
            ? '/brand/agri-safe-logo-dark.png'
            : '/brand/agri-safe-logo.png';
        }}
      />
      {showText && variant === 'full' && (
        <div className="flex flex-col">
          <span 
            className={`font-bold text-[var(--text-primary)] ${textSizeClasses[size]}`}
            style={{ color: brandConfig?.primary_color || 'var(--color-primary)' }}
          >
            Agri Safe
          </span>
        </div>
      )}
    </div>
  );
};

export default BrandLogo;